/**
 * Full-screen cinematic rank-up celebration.
 * Triggered globally via triggerRankUp(rankLabel, rankEmoji).
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sfxRankUp } from "@/lib/sfx";

// ─── Event bus ───────────────────────────────────────────────
type RankUpEvent = { label: string; emoji: string };
type Listener = (e: RankUpEvent) => void;
const listeners = new Set<Listener>();

export const triggerRankUp = (label: string, emoji: string) => {
  listeners.forEach((fn) => fn({ label, emoji }));
};

// ─── Particle burst ─────────────────────────────────────────
interface Spark {
  id: number;
  x: number;
  y: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
  delay: number;
}

let sparkId = 0;
const SPARK_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--concert-warm))",
  "hsl(var(--concert-glow))",
  "hsl(var(--concert-rose))",
  "#FFD700",
  "#FFA500",
  "#FF6B6B",
  "#7C3AED",
];

const generateSparks = (count: number): Spark[] =>
  Array.from({ length: count }, (_, i) => ({
    id: ++sparkId,
    x: 50 + (Math.random() - 0.5) * 10,
    y: 50 + (Math.random() - 0.5) * 10,
    angle: (360 / count) * i + (Math.random() - 0.5) * 20,
    distance: 30 + Math.random() * 45,
    size: 3 + Math.random() * 6,
    color: SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)],
    delay: Math.random() * 0.3,
  }));

// ─── Ring burst ─────────────────────────────────────────────
const Ring = ({ delay, size }: { delay: number; size: number }) => (
  <motion.div
    className="absolute left-1/2 top-1/2 rounded-full border-2 border-primary/60"
    initial={{ width: 0, height: 0, x: "-50%", y: "-50%", opacity: 0.8 }}
    animate={{
      width: size,
      height: size,
      x: "-50%",
      y: "-50%",
      opacity: 0,
    }}
    transition={{ duration: 1.2, delay, ease: "easeOut" }}
  />
);

// ─── Component ──────────────────────────────────────────────
const RankUpCelebration = () => {
  const [event, setEvent] = useState<RankUpEvent | null>(null);
  const [sparks, setSparks] = useState<Spark[]>([]);

  const handleEvent = useCallback((e: RankUpEvent) => {
    setEvent(e);
    setSparks(generateSparks(40));
    sfxRankUp();

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      setEvent(null);
      setSparks([]);
    }, 4000);
  }, []);

  useEffect(() => {
    listeners.add(handleEvent);
    return () => { listeners.delete(handleEvent); };
  }, [handleEvent]);

  return (
    <AnimatePresence>
      {event && (
        <motion.div
          className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => { setEvent(null); setSparks([]); }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-background/90 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Expanding rings */}
          <Ring delay={0} size={600} />
          <Ring delay={0.15} size={800} />
          <Ring delay={0.3} size={1000} />

          {/* Spark particles */}
          {sparks.map((s) => {
            const rad = (s.angle * Math.PI) / 180;
            const endX = Math.cos(rad) * s.distance;
            const endY = Math.sin(rad) * s.distance;
            return (
              <motion.div
                key={s.id}
                className="absolute rounded-full"
                style={{
                  left: `${s.x}%`,
                  top: `${s.y}%`,
                  width: s.size,
                  height: s.size,
                  backgroundColor: s.color,
                  boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
                }}
                initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                animate={{
                  scale: [0, 1.5, 0.8, 0],
                  x: `${endX}vw`,
                  y: `${endY}vh`,
                  opacity: [0, 1, 0.8, 0],
                }}
                transition={{
                  duration: 1.2,
                  delay: s.delay,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            );
          })}

          {/* Center content */}
          <motion.div
            className="relative z-10 flex flex-col items-center gap-4 text-center px-8"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
          >
            {/* Emoji with glow */}
            <motion.div
              className="relative"
              animate={{
                scale: [1, 1.15, 1],
                rotate: [0, -5, 5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="text-8xl sm:text-9xl drop-shadow-2xl">
                {event.emoji}
              </div>
              <motion.div
                className="absolute inset-0 rounded-full bg-primary/20 blur-3xl -z-10"
                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>

            {/* "RANK UP" label */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
            >
              <p className="text-xs sm:text-sm font-display font-bold uppercase tracking-[0.3em] text-primary">
                Rank Up
              </p>
            </motion.div>

            {/* Rank name */}
            <motion.h1
              className="text-4xl sm:text-6xl font-display font-black text-foreground"
              initial={{ y: 40, opacity: 0, scale: 0.8 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 0.45, type: "spring", stiffness: 200, damping: 15 }}
            >
              <span className="bg-gradient-to-r from-primary via-concert-warm to-concert-glow bg-clip-text text-transparent">
                {event.label}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-sm text-muted-foreground max-w-xs"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 0.8 }}
              transition={{ delay: 0.65 }}
            >
              You've reached a new tier. Keep the encore going!
            </motion.p>

            {/* Dismiss hint */}
            <motion.p
              className="text-xs text-muted-foreground/50 mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              tap anywhere to dismiss
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RankUpCelebration;
